/** Side drawer for a single preflight item on the cross-tenant Items page.
 *
 * Mirrors the Preflight tab's expanded row: status control + preview/run/mark-done/
 * view-log actions + the shared detail content (field editor, per-action UI,
 * dashboards, steps, error). Fetches the item fresh (seeded from the grid row) so
 * mutations reflect immediately. */
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { items, type PreflightItem } from '@/api/preflight';
import { PreflightItemActionBar } from '@/features/preflight/PreflightItemActionBar';
import { PreflightStatusControl } from '@/features/preflight/PreflightStatusControl';
import { STATUS_LABELS, statusChipColor } from '@/features/preflight/status';
import { PreflightItemDetailContent } from '@/features/tour-operators/tabs/PreflightItemDetailContent';
import { PreflightItemRunLogDrawer, PreflightPreviewDialog } from '@/features/tour-operators/tabs/PreflightRunLog';
import { usePreflightItemActions } from '@/features/tour-operators/tabs/usePreflightItemActions';
import { usePermissions } from '@/hooks/usePermissions';

interface Props {
  /** The clicked grid row (null ⇒ closed). Used to seed the fresh fetch. */
  selected: PreflightItem | null;
  onClose: () => void;
  /** Invalidate the Items grid after a mutation. */
  onChanged: () => void;
}

export function PreflightItemDrawer({ selected, onClose, onChanged }: Props) {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_preflight_manage');
  const qc = useQueryClient();
  const [logOpen, setLogOpen] = useState(false);

  const topId = selected?.tour_operator?.id ?? 0;
  const id = selected?.id ?? 0;

  const { data } = useQuery({
    queryKey: ['preflight', 'item', topId, id],
    queryFn: () => items.retrieve(topId, id),
    enabled: selected != null && topId > 0,
    initialData: selected ?? undefined,
  });

  const actions = usePreflightItemActions(topId, () => {
    void qc.invalidateQueries({ queryKey: ['preflight', 'item', topId, id] });
    onChanged();
  });

  const item = data ?? selected;

  return (
    <>
      <Drawer
        anchor="right"
        open={selected != null}
        onClose={onClose}
        sx={{ zIndex: (t) => t.zIndex.modal }} // above the fixed AppBar (matches the run-log drawer)
        PaperProps={{ sx: { width: { xs: '100%', sm: 620 } } }}
      >
        {item && (
          <Box sx={{ p: 2.5 }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1 }}>
              <Box sx={{ pr: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                  {item.title || item.action}
                </Typography>
                {item.tour_operator && (
                  <Typography variant="caption" color="text.secondary">
                    {item.tour_operator.name} · {item.tour_operator.code} · {item.group_name}
                  </Typography>
                )}
              </Box>
              <IconButton size="small" onClick={onClose} aria-label="Close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
              {canManage ? (
                <PreflightStatusControl
                  status={item.status}
                  disabled={actions.isBusy(item.id)}
                  onComplete={() => actions.onComplete(item.id)}
                  onStatusChange={(s) => actions.onStatusChange(item.id, s)}
                />
              ) : (
                <Chip size="small" color={statusChipColor(item.status)} label={STATUS_LABELS[item.status]} />
              )}
              <PreflightItemActionBar
                item={item}
                canManage={canManage}
                busy={actions.isBusy(item.id)}
                onPreview={() => actions.onPreview(item.id)}
                onRun={() => actions.onRun(item.id)}
                onExecute={() => actions.onExecute(item.id)}
                onComplete={() => actions.onComplete(item.id)}
                onViewLog={() => setLogOpen(true)}
              />
            </Stack>

            {!item.applicable && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Not applicable to this tour operator{item.applicable_reason ? `: ${item.applicable_reason}` : '.'}
              </Typography>
            )}

            <Divider sx={{ mb: 1.5 }} />

            <PreflightItemDetailContent
              item={item}
              topId={topId}
              toCode={item.tour_operator?.code ?? ''}
              canManage={canManage}
              pending={actions.isBusy(item.id)}
              onConfigure={(values) => actions.onConfigure(item.id, values)}
              onStepChange={(step, status) => actions.onStepChange(item.id, step, status)}
              lastSave={actions.lastConfigureRun(item.id)}
              onDismissLastSave={() => actions.dismissConfigureRun(item.id)}
              onChanged={() => {
                void qc.invalidateQueries({ queryKey: ['preflight', 'item', topId, id] });
                onChanged();
              }}
            />
          </Box>
        )}
      </Drawer>

      <PreflightItemRunLogDrawer
        topId={topId}
        itemId={logOpen ? id : null}
        title={item?.title}
        open={logOpen}
        onClose={() => setLogOpen(false)}
      />
      <PreflightPreviewDialog run={actions.previewRun} onClose={actions.clearPreview} />
    </>
  );
}
